import { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createClient,
  checkClientInn,
  checkClientCode,
  getAssignableOwners,
} from '../../shared/api/clients.api'
import type { InnCheckResult } from '../../shared/api/clients.api'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import type { ClientCategory } from '../../shared/api/types'
import { normalizeCity, SEGMENT_OPTIONS } from '../../shared/utils/clientFields'
import { useAuthStore } from '../auth/authStore'
import { askSaveDespiteDuplicateInn } from './duplicateInn'

export function NewClientPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuthStore()

  // Роль ASSISTANT (Помощник): клиент обязан принадлежать одному из
  // прикреплённых менеджеров — себя помощник выбрать не может.
  const isAssistant = user?.role === 'ASSISTANT'
  const assistantManagers = user?.assistantManagers ?? []
  // Бухгалтер (14.10.2026) тоже выбирает владельца — из всех менеджеров и РОП.
  const isAccountant = user?.role === 'ACCOUNTANT'
  const ownersQuery = useQuery({
    queryKey: ['clients-assignable-owners'],
    // Список нужен только бухгалтеру — функцию трогаем лишь при запросе.
    queryFn: () => getAssignableOwners(),
    enabled: isAccountant,
  })
  const needsOwner = isAssistant || isAccountant
  const ownerOptions = isAccountant
    ? (ownersQuery.data ?? []).map((o) => ({ id: o.id, name: o.name, email: o.email }))
    : assistantManagers

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [category, setCategory] = useState<ClientCategory>('C')
  const [notes, setNotes] = useState('')
  const [inn, setInn] = useState('')
  const [city, setCity] = useState('')
  const [segment, setSegment] = useState('')
  const [ownerManagerId, setOwnerManagerId] = useState(
    isAssistant ? (assistantManagers[0]?.id ?? '') : '',
  )
  const [innConflict, setInnConflict] = useState<InnCheckResult | null>(null)
  const [codeConflict, setCodeConflict] = useState<InnCheckResult | null>(null)
  const innTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const codeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const mutation = useMutation({
    mutationFn: (confirmDuplicateInn: boolean) =>
      createClient({
        name: name.trim(),
        code: code.trim() || undefined,
        category,
        notes: notes.trim() || undefined,
        inn: inn.trim() || null,
        city: normalizeCity(city) || null,
        segment: segment || null,
        ...(needsOwner && ownerManagerId ? { managerId: ownerManagerId } : {}),
        ...(confirmDuplicateInn ? { confirmDuplicateInn: true } : {}),
      }),
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void navigate(`/clients/${client.id}`)
    },
  })

  // Такой ИНН уже есть — сервер отвечает 409 DUPLICATE_INN; после
  // подтверждения повторяем с флагом (решение Сергея 14.09.2026).
  const submit = async (confirmDuplicateInn = false) => {
    try {
      await mutation.mutateAsync(confirmDuplicateInn)
    } catch (err) {
      if (!confirmDuplicateInn && (await askSaveDespiteDuplicateInn(err))) await submit(true)
    }
  }

  // Дебаунс-проверка кода клиента
  useEffect(() => {
    if (codeTimerRef.current) clearTimeout(codeTimerRef.current)
    const trimmed = code.trim()
    if (!trimmed) {
      setCodeConflict(null)
      return
    }
    codeTimerRef.current = setTimeout(async () => {
      const result = await checkClientCode(trimmed)
      setCodeConflict(result)
    }, 500)
    return () => {
      if (codeTimerRef.current) clearTimeout(codeTimerRef.current)
    }
  }, [code])

  // Дебаунс-проверка ИНН
  useEffect(() => {
    if (innTimerRef.current) clearTimeout(innTimerRef.current)
    const trimmed = inn.trim()
    if (trimmed.length < 10) {
      setInnConflict(null)
      return
    }
    innTimerRef.current = setTimeout(async () => {
      const result = await checkClientInn(trimmed)
      setInnConflict(result)
    }, 500)
    return () => {
      if (innTimerRef.current) clearTimeout(innTimerRef.current)
    }
  }, [inn])

  const canSubmit =
    name.trim().length >= 2 &&
    !mutation.isPending &&
    !codeConflict &&
    // Дубль ИНН не блокирует кнопку: подсказка ниже поля — предупреждение,
    // сохранение — после подтверждения (submit).
    (!needsOwner || !!ownerManagerId)

  return (
    <div className="max-w-lg space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/clients" className="text-gray-400 hover:text-gray-600 text-sm">
          ← Клиенты
        </Link>
        <h1 className="text-xl font-semibold text-gray-900">Новый клиент</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        {needsOwner && (
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Клиент принадлежит менеджеру <span className="text-red-500">*</span>
            </label>
            <select
              value={ownerManagerId}
              onChange={(e) => setOwnerManagerId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              <option value="" disabled>
                Выберите менеджера…
              </option>
              {ownerOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.email}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Название <span className="text-red-500">*</span>
          </label>
          <input
            data-testid="client-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="ООО «Оборонстрой»"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Код клиента</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ROG001"
              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 ${codeConflict ? 'border-amber-400' : 'border-gray-300'}`}
            />
            {codeConflict && (
              <div className="mt-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                Клиент с таким кодом уже существует:{' '}
                <strong>
                  <Link
                    to={`/clients/${codeConflict.id}`}
                    className="underline hover:text-amber-900"
                  >
                    {codeConflict.name}
                  </Link>
                </strong>
                {'. Менеджер: '}
                <strong>{codeConflict.manager.name || codeConflict.manager.email}</strong>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Категория</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ClientCategory)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              <option value="A">A — Ключевой заказчик</option>
              <option value="B">B — Растущий клиент</option>
              <option value="C">C — Новый заказчик</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">ИНН</label>
            <input
              type="text"
              value={inn}
              onChange={(e) => setInn(e.target.value.replace(/\D/g, ''))}
              placeholder="7712345678"
              maxLength={12}
              className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 ${innConflict ? 'border-amber-400' : 'border-gray-300'}`}
            />
            {innConflict && (
              <div className="mt-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                Клиент с таким ИНН уже существует:{' '}
                <strong>
                  <Link
                    to={`/clients/${innConflict.id}`}
                    className="underline hover:text-amber-900"
                  >
                    {innConflict.name}
                  </Link>
                </strong>
                {'. Менеджер: '}
                <strong>{innConflict.manager.name || innConflict.manager.email}</strong>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Город</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              onBlur={(e) => {
                const norm = normalizeCity(e.target.value)
                if (norm) setCity(norm)
              }}
              placeholder="Москва"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Сегмент</label>
          <select
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
          >
            <option value="">— не выбран —</option>
            {SEGMENT_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Заметки</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Необязательно..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
          />
        </div>
      </div>

      {mutation.isError && <ErrorMessage error={mutation.error} />}

      <div className="flex gap-3">
        <button
          data-testid="client-submit"
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="px-5 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          {mutation.isPending ? 'Создание...' : 'Создать клиента'}
        </button>
        <Link
          to="/clients"
          className="px-5 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
        >
          Отмена
        </Link>
      </div>
    </div>
  )
}
